/**
 * frontend/src/pages/valuation/_DriverQuestionBlock.tsx
 *
 * One driver question: prompt, required mark, chips, the ⓘ help, "I'm not
 * sure", and the answer options. Split out of `_DriverQuestionnaire.tsx`
 * (VAL-PATH P5), which keeps the steps, the gating and the deferred state.
 *
 * VAL-PATH P5 / I28 — help text is the ⓘ (`InfoDot`), named after the
 * question, in place of the old "Why we ask" toggle.
 */
import type { DriverQuestion } from '@/types/valuation'
import { InfoDot } from '@/components/ui/info-dot'
import { humanizeDriver } from './_IntakeDrivers'
import { DeferControl, isDocumentAnswerable, type DeferredAttachment } from './_DriverQuestionDefer'

export function QuestionBlock({
  question,
  answer,
  onAnswer,
  deferred,
  attachment,
  onDefer,
  onCancelDefer,
  onAttach,
  canSeePoints,
  failing = false,
}: {
  question: DriverQuestion
  answer: string | undefined
  onAnswer: (questionId: string, value: string) => void
  deferred: boolean
  attachment: DeferredAttachment | undefined
  onDefer: (questionId: string) => void
  onCancelDefer: (questionId: string) => void
  onAttach: (questionId: string, file: DeferredAttachment) => void
  /** R3 — the seller (valuation_owner) never sees which answer scores
   * higher; valuation_signer/firm/admin need the scoring key to review a run.
   * Removed from the DOM entirely when false, not merely CSS-hidden. */
  canSeePoints: boolean
  /** WALKFIX-A AMEND-2 — named by a refused run, or a saved answer that is
   * no longer a valid choice: red border + red prompt (owner rule 10-15). */
  failing?: boolean
}) {
  const derived = isDocumentAnswerable(question)
  // R3e/S18 -- a deferred document-answerable question cannot be resolved by
  // hand. Radios stay disabled until a document is attached. Branch 2 never
  // disables (R3f -- there is no document path to wait on).
  const radiosDisabled = derived && deferred && !attachment
  return (
    // R11 — no score number renders here for any role; `canSeePoints` is
    // kept wired through as a real DOM signal (not a display) rather than
    // deleted, since it is still the correct place to hang a future
    // role-gated scoring-key feature if one comes back.
    <fieldset
      id={`question-${question.question_id}`}
      tabIndex={-1}
      className={`rounded-lg border ${failing ? 'border-red-600' : 'border-gray-200'} p-3`}
      data-scoring-key-visible={canSeePoints}
      data-failing={failing ? 'true' : undefined}
    >
      <legend className={`px-1 text-sm font-medium ${failing ? 'text-red-700' : 'text-gray-900'}`}>
        {question.prompt}
        {question.required && (
          <>
            <span aria-hidden="true" className="text-red-600"> *</span>
            <span className="sr-only"> (required)</span>
          </>
        )}
        {question.help_text && (
          <InfoDot label={question.prompt} className="ml-1">
            {question.help_text}
          </InfoDot>
        )}
      </legend>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-pal-ink-muted">
        <span className="rounded bg-pal-surface-muted px-1.5 py-0.5">
          {humanizeDriver(question.driver)}
        </span>
        {!question.required && (
          <span className="rounded bg-pal-info-bg px-1.5 py-0.5 text-pal-link">
            corroborating — doesn't change the score
          </span>
        )}
        {derived && (
          <span
            className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700"
            title="The documents on file should answer this — confirm against them rather than estimating."
          >
            check the documents
          </span>
        )}
      </div>
      <DeferControl
        question={question}
        deferred={deferred}
        attachment={attachment}
        onDefer={onDefer}
        onCancelDefer={onCancelDefer}
        onAttach={onAttach}
      />
      <div className={`space-y-1.5 ${radiosDisabled ? 'opacity-50' : ''}`}>
        {question.options.map(opt => {
          const checked = answer === opt.value
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
                radiosDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${checked ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}
            >
              <input
                type="radio"
                className="mt-1 shrink-0"
                name={question.question_id}
                value={opt.value}
                checked={checked}
                disabled={radiosDisabled}
                onChange={() => onAnswer(question.question_id, opt.value)}
              />
              <span className="flex-1 text-pal-ink">{opt.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
